import { Check, X, AlertTriangle, InfoIcon } from 'lucide-react';

const Pricing = () => {
  const tiers = [
    {
      name: 'Essential Plan',
      price: '$69',
      frequency: 'per month',
      description: 'For businesses that need occasional website help or minor fixes each month',
      features: [
        '3 support credits per month',
        'No custom development credits'
      ],
      notIncluded: [
        'Custom development'
      ],
      buttonText: 'Choose Essential Plan',
      highlighted: false
    },
    {
      name: 'Growth Plan',
      price: '$179',
      frequency: 'per month',
      description: 'Great for growing teams that need reliable support and small website improvements',
      features: [
        '10 support credits per month',
        '5 custom development credits'
      ],
      notIncluded: [],
      buttonText: 'Choose Growth Plan',
      highlighted: true,
      savingsText: 'Most Popular'
    },
    {
      name: 'Pro Team Plan',
      price: '$379',
      frequency: 'per month',
      description: 'Made for busy sites or agencies managing multiple websites with regular support and development needs',
      features: [
        '30 support credits per month',
        '10 custom development credits'
      ],
      notIncluded: [],
      buttonText: 'Choose Pro Team Plan',
      highlighted: false,
      isBestValue: true,
      savingsText: 'Best Value'
    }
  ];

  const additionalCredits = [
    {
      type: 'Support Credits',
      options: [
        { amount: '3 credits', price: '$69' },
        { amount: '10 credits', price: '$179' },
        { amount: '20 credits', price: '$349' }
      ]
    },
    {
      type: 'Custom Development Credits',
      options: [
        { amount: '5 credits', price: '$99' },
        { amount: '10 credits', price: '$189' },
        { amount: '20 credits', price: '$369' }
      ]
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-white relative z-10">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 animate-fadeIn">
          <span className="text-primary font-semibold text-sm uppercase tracking-wider mb-2 block">Plans & Pricing</span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Website Support Plans
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the right level of support for your website needs
          </p>
        </div>

        {/* Credit explanation box */}
        <div className="max-w-4xl mx-auto mb-12 bg-blue-50 rounded-lg p-8 border border-blue-100 animate-fadeIn shadow-sm">
          <div className="flex items-start gap-4">
            <InfoIcon className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">How Our Credit System Works</h3>
              <p className="text-gray-700 mb-5">Our support is based on a credit system for maximum flexibility. Each support credit equals up to 1 hour of support time. <span className="font-semibold">Adding your website and creating tickets are free, and the initial diagnosis is also free.</span> Credit cost will be calculated during this initial diagnosis, so there will be no surprises.</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Support Credits</h4>
                  <p className="text-gray-600 mb-2">Used for:</p>
                  <ul className="space-y-1 mb-4">
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">Bug fixes</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">Content updates</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">Maintenance tasks</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Custom Development Credits</h4>
                  <p className="text-gray-600 mb-2">Used for:</p>
                  <ul className="space-y-1 mb-4">
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">New feature development</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">Custom integrations</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">Major design changes</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-500 italic">Custom credits will be charged after assessing your request</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {tiers.map((tier, index) => (
            <div 
              key={index}
              className={`rounded-xl overflow-hidden border transition-all duration-300 flex flex-col ${
                tier.highlighted 
                  ? 'border-primary shadow-xl relative z-10 scale-105' 
                  : tier.isBestValue
                    ? 'border-indigo-200 shadow-md hover:shadow-lg hover:-translate-y-1'
                    : 'border-gray-200 shadow-md hover:shadow-lg hover:-translate-y-1'
              }`}
            >
              {(tier.highlighted || tier.isBestValue) && (
                <div className="relative">
                  <div className={`${tier.highlighted ? 'bg-primary' : 'bg-indigo-700'} text-white text-center py-1 font-medium`}>
                    <span className={`inline-flex items-center ${tier.highlighted ? 'text-yellow-200' : 'text-white'} mr-1`}>
                      {tier.highlighted ? '★' : '✓'}
                    </span>
                    {tier.savingsText || (tier.highlighted ? 'Most Popular' : 'Best Value')}
                  </div>
                </div>
              )}
              
              <div className="p-8 flex flex-col h-full">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{tier.name}</h3>
                  <div className="flex items-center justify-center gap-1 mb-4">
                    <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                    <span className="text-gray-600">{tier.frequency}</span>
                  </div>
                  <p className="text-gray-600 h-14 flex items-center justify-center">{tier.description}</p>
                </div>
                
                <div className="mb-8 flex-grow">
                  <p className="font-medium text-gray-900 mb-4">What's included:</p>
                  <ul className="space-y-2">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {tier.notIncluded.length > 0 && (
                  <div className="mb-8">
                    <p className="font-medium text-gray-900 mb-4">Not included:</p>
                    <ul className="space-y-2">
                      {tier.notIncluded.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <X className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-500">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mt-auto pt-8">
                  <a 
                    href="/dashboard/settings?tab=products"
                    className={`w-full py-3 px-6 rounded-md font-medium transition-colors inline-block text-center ${
                      tier.highlighted
                        ? 'bg-primary text-white hover:bg-primary/90 shadow-md'
                        : tier.isBestValue
                          ? 'bg-indigo-700 text-white hover:bg-indigo-800 shadow-md'
                          : 'bg-white text-primary border border-primary hover:bg-primary/5'
                    }`}
                  >
                    {tier.buttonText}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Additional credits pricing */}
        <div className="mt-20 max-w-4xl mx-auto bg-gray-50 rounded-xl p-8 border border-gray-100 animate-fadeIn">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Additional Credits</h3>
          <p className="text-gray-600 mb-8 text-center">Need more support or development time? Purchase additional credits at any time.</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {additionalCredits.map((creditType, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h4 className="font-bold text-lg text-gray-900 mb-4">{creditType.type}</h4>
                <ul className="space-y-4">
                  {creditType.options.map((option, idx) => (
                    <li key={idx} className="flex items-center justify-between pb-3 border-b border-gray-100">
                      <span className="font-medium text-gray-800">{option.amount}</span>
                      <span className="text-primary font-bold">{option.price}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 text-center">
                  <a 
                    href="/dashboard/settings?tab=products"
                    className="inline-block w-full bg-primary text-white px-6 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors"
                  >
                    Purchase Credits
                  </a>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-gray-500 text-sm mt-6 text-center">
            Support credits and custom development credits are sold separately and can be purchased at any time.
          </p>
        </div>
        
        <div className="mt-20 max-w-3xl mx-auto bg-amber-50 border border-amber-200 rounded-xl p-8 animate-fadeIn shadow-md">
          <div className="flex flex-col md:flex-row items-start md:space-x-6">
            <div className="bg-amber-100 p-4 rounded-lg flex items-center justify-center mb-4 md:mb-0">
              <AlertTriangle className="h-12 w-12 text-amber-600 flex-shrink-0" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">One-Time Fix: $89</h3>
              <p className="text-gray-600 mb-5">
                Need urgent help with a specific website issue? Our one-time fix service provides immediate assistance without a monthly commitment. <span className="font-semibold">This is equivalent to 3 support credits</span> and is good for 1 time fix of issues. If the issue cost is more than 3 credits, it is advisable to purchase more one-time credits if you don't wish to avail subscription plans. Perfect for quick help without a subscription. Ideal for one-off troubleshooting, bug fixes, or urgent support needs.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Quick response (usually within hours)</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Focused on a single specific issue</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Simple payment process</span>
                </li>
              </ul>
              <a href="/dashboard/settings?tab=products" className="inline-block bg-amber-600 text-white px-8 py-3 rounded-md font-medium hover:bg-amber-700 transition-colors shadow-sm">
                Get Emergency Fix Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing; 